// Teacher-hours copy — EN, verbatim from the ratified golden mock
// ~/mockups/2026-09/teacher-hours-week.html (picker/spend copy = Luna's EN).
// Keys identical to teacherHours.nl.ts (parity-tested).
import type { TeacherHoursDictionary } from './teacherHoursTypes';

export const teacherHoursEn: TeacherHoursDictionary = {
  units: { hoursShort: ' h', free: ' free', extra: ' extra', hoursWord: '{{n}} hours', zeroHours: '0 hours' },
  weekdayShort: { sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat' },
  weekdayLong: { sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday' },
  monthShort: { jan: 'Jan', feb: 'Feb', mar: 'Mar', apr: 'Apr', may: 'May', jun: 'Jun', jul: 'Jul', aug: 'Aug', sep: 'Sep', oct: 'Oct', nov: 'Nov', dec: 'Dec' },
  monthLong: {
    jan: 'January', feb: 'February', mar: 'March', apr: 'April', may: 'May', jun: 'June',
    jul: 'July', aug: 'August', sep: 'September', oct: 'October', nov: 'November', dec: 'December',
  },
  leaveTypes: {
    vacation: 'Holiday',
    appointment: 'Doctor or dentist',
    care_short: 'Care for someone',
    special: 'Special leave',
  },
  leaveTypesShort: { vacation: 'Holiday', appointment: 'Doctor', care_short: 'Care', special: 'Special' },
  hoursKinds: {
    meeruren: 'Extra time',
    correction: 'Worked less',
    break_edit: 'Missed break',
    plus_payout: 'Cash out extra hours',
    plus_verlof: 'Extra hours to time off',
  },
  zeroReasons: {
    no_roster_yet_many: 'No roster on these dates, so 0 h deducted',
    no_roster_yet_one: 'No roster on this date, so 0 h deducted',
    one_off_only: 'Your roster only had this as a one-off day, so 0 h deducted',
    no_regular_roster: 'No regular roster found, so 0 h deducted',
  },
  notes: {
    movedFromPortabase: 'Leave (moved over from Portabase)',
    requestedInApp: 'Requested in the app',
    removedByManager: 'Removed from your roster by your manager',
    shorterDay: 'Shorter day {{from}}–{{to}}',
    overlapFuneral: 'Same day as your funeral leave on {{date}}: deducted once ({{hours}})',
    convertedToCare: 'Turned into care for someone',
    convertedFromExtra: 'Turned from extra time: “{{reason}}”',
  },
  shell: {
    role: { teacher: 'Teacher', manager: 'Manager (glimpse)' },
    hello: 'Hi {{name}}',
    contractMeta: 'Contract {{hours}} h/week · standing roster {{days}} h net',
    provisional: 'provisional',
    tabs: { week: 'My Week', year: 'My Year', gewerkt: 'Worked', requests: 'Requests' },
    myBalance: 'My balance',
    newRequest: 'New request',
  },
  dock: {
    leaveRequested: ' leave requested',
    extraAwaiting: ' extra awaiting approval',
    questionTitle: 'Your manager has a question',
    about: 'about',
    answer: 'Answer',
  },
  week: {
    planningNoteLegend: 'note from planning',
    prev: 'Previous week',
    next: 'Next week',
    week: 'Week',
    thisWeek: ' · this week',
    earlierWeeksNote:
      'Earlier weeks are not shown here: your roster is only in the system from 15 Jun, and until the end of July only as one-off days. Your holidays from that time are under <b>Requests</b>, flagged.',
    sum: { contract: 'Contract', roster: 'Roster', away: 'Away', yearHours: 'Year hours' },
    openRequestsTitle: '{{count}} earlier requests are still awaiting approval',
    openRequestsTail: ', the oldest since {{date}} ({{days}} days). View ›',
    patternLine: 'Standing roster {{pattern}}, contract {{contract}} ({{gap}}). With your days ({{days}}) this week counts {{week}}.',
    pendingLine: 'Plus {{hours}} awaiting approval (striped, not counted yet).',
    dayOff: 'Day off · not rostered',
    holidayCounts: 'counts 0 h',
    noRosterKnown: 'No roster known',
    publicHoliday: 'Public holiday',
    breakSuffix: ' · 60 min break',
    net: 'net',
    planned: 'planned',
    unknownGroup: 'group not found',
    footer:
      'Tap a day to add extra time, ask for time off or fix your hours.<br/>± = what this day does to your year hours (roster − standing roster + approved extra time).',
  },
  chips: {
    awaitingApproval: 'awaiting approval',
    bookedTwiceOnce: 'booked twice · deducted once',
    zeroFromBalance: '0 h from your balance',
    partlyFromExtra: ' · partly from extra hours',
    fromExtra: ' · from extra hours',
    approved: 'approved',
    addedByManager: ' · added by your manager',
    declined: 'declined',
    managerHasQuestion: 'your manager has a question',
    alsoHolidayDay: 'also a holiday day',
    onHolidayDay: 'on a holiday day',
    priority: 'Priority',
    autoApproved: 'Automatically approved',
    zeroDeducted: '0 h deducted',
    bookedTwice: 'booked twice',
    notFromBalance: 'not from your balance',
    fromExtraHours: 'from extra hours',
    awaitingPayroll: 'awaiting payroll',
    holidayDay: 'holiday day',
    twiceOnce: 'twice · deducted once',
  },
  decision: {
    none: 'Decision: not from your balance',
    vacationPlus: 'Decision: added to your holiday hours',
    vacationMinus: 'Decision: from your holiday hours',
    extraPlus: 'Decision: added to your extra hours',
    extraMinus: 'Decision: from your extra hours',
    split: 'Decision: {{vacation}} holiday + {{extra}} extra hours',
  },
  status: {
    pending: 'awaiting approval',
    question: 'question from your manager',
    approved: 'approved',
    paidOut: 'paid out',
    rejected: 'declined',
    withdrawn: 'withdrawn',
  },
  timeline: {
    sent: 'Sent',
    withManager: 'With your manager',
    declined: 'Declined',
    withdrawn: 'Withdrawn',
    decision: 'Decision',
    inSalary: 'In your salary',
  },
  history: {
    managerReason: 'Reason from your manager:',
    managerNote: 'Note from your manager:',
    yourAnswer: 'Your answer:',
    openSince: 'open since',
    days: 'days',
    withdraw: 'Withdraw',
    requested: 'requested',
    toExtraStatutory: ' · added to your extra-statutory holiday hours',
    withNextSalary: ' · with your next salary',
    zeroNoteTail:
      'So your balance is too high right now. HR will correct this once your old roster is in; until then your balance is <b>provisional</b>.',
    filters: { all: 'All', waiting: 'Waiting', approved: 'Approved', declined: 'Declined', check: 'Check' },
    checkNote:
      "These aren't your mistakes. The system shows them instead of quietly leaving them out. Your manager or HR will put them right.",
    emptyTitle: 'Nothing here',
    emptyBody: 'No requests in this list.',
    withdrawnToast: 'Request withdrawn',
  },
  day: {
    week: 'Week',
    past: 'past',
    planned: 'planned',
    roster: 'Roster',
    noRosterYet: 'no roster in the system yet',
    minusContract: 'Minus your standing roster for this day',
    holidayCounts: ': counts 0 h',
    yourRegular: 'your regular ',
    approvedExtra: 'Approved extra time / corrections',
    shorterCovered: 'Shorter day covered by leave',
    notMinusHours: 'does not count as minus hours',
    total: 'This day for your year hours',
    awaitingSuffix: ' (awaiting approval)',
    leaveCountsWorked: 'leave counts as worked for your year hours',
    removedNote:
      'This day was removed from your roster, so −{{hours}} on your year hours. Did you work anyway or were you off? That is below, under your requests.',
    twoLeaveTitle: 'Two leave requests on this day.',
    managerTidies: 'Your manager will tidy up the duplicate.',
    onThisDay: 'On this day',
    where: 'Where',
    planningNote: 'Note from planning',
    planningNoteClose: 'tap outside to close',
    whatToDo: 'What would you like to do?',
    extra: {
      title: 'Record extra time',
      blockedHoliday: 'Not possible: this is a holiday day. Not right? Ask your manager to change it.',
      blockedPublicHoliday: 'Public holiday: ask your manager',
      hint: 'Team meeting, covering, started early or stayed late',
    },
    off: {
      title: 'Ask for time off',
      past: 'This day has passed',
      alreadyLeave: 'You already have leave on this day',
      notWorking: "You don't work this day",
      hint: 'Whole day or part, from holiday or extra hours',
    },
    fix: {
      title: 'Fix my hours',
      blockedHoliday: 'On a holiday day: ask your manager to shorten the leave',
      hint: 'Worked less, missed a break or your roster was different',
    },
  },
  leaveForm: {
    back: 'Back',
    hoursFromRoster: 'hours come from your roster',
    prioNote:
      'By default this is <b>not</b> taken from your holiday balance. Your request goes to your manager as a priority; they decide how it is settled and may ask for proof.',
    callManager: 'Your shift starts soon? Call your manager too, so they can arrange cover.',
    appointmentNote: 'By default from your holiday hours. Your manager decides how it is settled.',
    from: 'From',
    until: 'Until',
    wholeDay: 'Whole day',
    partOfDay: 'Part of the day',
    hoursOff: 'Hours off',
    workUntilFrom: 'I work until / from',
    workUntilPlaceholder: 'e.g. until 14:00',
    workUntil: 'until',
    workFrom: 'from',
    noWorkingDays: 'No working days in this period.',
    noFixedRoster: 'No fixed roster — your manager sets it up; the hours are calculated after that.',
    afterContract: 'After your current contract (until {{date}}). Preliminary: these days count once your contract is extended.',
    noContractAfterEnd: 'According to your contract details, your contract ran through {{date}}. No active contract is registered for the selected period. Contact your manager.',
    noActiveContract: 'No active contract is registered for the selected period. Contact your manager.',
    offDayZero: '{{name}} 0 h',
    closureDay: 'closure day',
    takeFrom: 'Take from',
    source: { vacation: 'Holiday', extra: 'Extra hours', both: 'Both' },
    extraAvailable: 'available',
    stillAwaiting: 'still awaiting approval',
    pastBlocked: 'This day has passed. Time off afterwards? Choose <b>Care</b> or <b>Special</b>, or ask your manager.',
    beyondHorizon: 'You can request time off up to 31 December {{year}}.',
    alreadyLeaveOn: 'You already have leave on',
    costs: 'This costs',
    holiday: 'holiday',
    extraHours: 'extra hours',
    afterApproval: 'After approval',
    free: 'free',
    stripedUntil: 'Until then it stays striped.',
    negativeWarning: 'Your balance goes negative. Your manager decides on approval (work it back or deduct it, see the handbook).',
    openingInReview: 'Your balance may still change: the 2026 opening balance is still in review.',
    message: 'Message to your manager (optional)',
    messagePlaceholder: "E.g. who you've already arranged this with",
    cancel: 'Cancel',
    send: 'Send request',
    sentPriority: 'Sent to your manager as a priority request',
    sent: 'Sent to your manager for approval',
  },
  extraForm: {
    title: 'Record extra time',
    crumb: 'Fill in when you actually did it, even if that was before your shift.',
    date: 'Date',
    from: 'From',
    until: 'Until',
    whatFor: 'What for',
    presets: {
      teamMeeting: 'Team meeting',
      covering: 'Covering for a colleague',
      startedEarly: 'Started early',
      stayedLate: 'Stayed late',
      training: 'Training / course',
    },
    notePlaceholder: 'Short note (optional)',
    countsAsWork:
      'Counts as work time: meetings, training, opening and closing, picking up and dropping off children. Caring for someone or an appointment? That is <b>leave</b>, not extra time.',
    blockedHoliday:
      "You're on holiday on {{date}}. Extra time on a holiday day isn't possible. Holiday not right? Ask your manager to change it.",
    blockedFuture: 'Record extra time on or after the day itself.',
    blockedInsideRoster: 'This falls within your roster ({{from}}–{{to}}). Extra time is time outside your shift.',
    preview: 'after approval. Until then it shows striped on',
    cancel: 'Cancel',
    send: 'Send',
  },
  fixForm: {
    title: 'Fix my hours',
    workedLess: 'Worked less',
    missedBreak: 'Missed a break',
    date: 'Date',
    howManyLess: 'How many hours less than your roster?',
    howMuchBreak: 'How much break did you miss?',
    whatHappened: 'What happened?',
    placeholderLess: 'E.g. after-school care closed early',
    placeholderBreak: 'E.g. alone with the group',
    preview: 'on your year hours after approval.',
    cancel: 'Cancel',
    send: 'Send',
  },
  picker: {
    title: 'What would you like to do?',
    tip: 'Tip: tap a day in My Week to start with the date already filled in.',
    timeOff: 'Time off',
    vacation: { title: 'Holiday', body: 'Time to recharge! Pick your dates and send them to your manager for approval.' },
    appointment: {
      title: 'Doctor or dentist',
      body: 'Looking after yourself matters too. Request the time you need for your manager to review.',
    },
    care_short: {
      title: 'Care for someone',
      body: 'Someone close to you needs care. Tell us what you need — your manager will receive a priority request.',
    },
    special: {
      title: 'Special leave',
      body: "For life's big moments and difficult goodbyes. Tell us what you need — your request goes to your manager's priority list.",
    },
    priority: 'Priority',
    spend: {
      title: 'Use extra hours',
      zero: 'Your balance is 0 hours. Once you have hours available, you can request time off here.',
      body: "You've put in the time. Request some back as time off, with your manager's approval.",
      available: 'Available to use',
      awaiting: '{{hours}} still awaiting approval.',
    },
    hoursWorked: 'Hours worked',
    extra: {
      title: 'Record extra time',
      body: 'Every extra bit counts. Add your start and end time — including time worked before or after your shift — for your manager to review.',
    },
    fix: {
      title: 'Fix my hours',
      body: 'Worked less, missed a break or spotted a mistake? Tell us what happened so your manager can review the correction.',
    },
    sick: 'Feeling unwell yourself? Call your manager before 7:00 am so we can arrange cover. Sickness must be reported by phone.',
  },
  spend: {
    title: 'Use extra hours',
    approved: 'approved',
    alreadyRequested: 'already requested',
    stillAwaiting: 'still awaiting approval',
    zero: 'Your balance is 0 hours. Once you have hours available, you can request time off here.',
    seeWaiting: "See what's waiting",
    verlof: { title: 'Take as time off', body: 'All {{hours}} added as time-off hours' },
    part: { title: 'Take part as time off', body: 'Choose how much of your {{hours}}' },
    timeOffHours: 'Time off (h)',
    total: 'Total',
    of: 'of',
    policy: 'Hours are taken as time off, never paid out; what you do not take stays as free time.',
    agreeTogether: 'You and your manager agree on this together: it only comes off your extra hours after approval.',
    cancel: 'Cancel',
    send: 'Send',
    sent: 'Sent to your manager for approval',
  },
  receipt: {
    why: 'Why',
    asOfToday: 'As of today',
    workingDaysOf: 'working days of on average',
    carriedOver: 'Carried over from {{year}}',
    builtUp: 'Built up {{year}} (yearly entitlement)',
    statutory: 'statutory',
    extraStatutory: 'extra-statutory',
    freeToTake: 'Free to take',
    withheld: 'Balance not available yet',
    zeroHolidaysTitle: '{{count}} holidays counted 0 h',
    extraHours: 'Extra hours',
    approvedMinusLess: 'Approved extra − worked less',
    meeruren: 'meeruren',
    used: 'Used (time off or cashed out)',
    awaitingApproval: 'Awaiting approval',
    requests: 'requests',
    extraNegative: 'Your extra hours are below zero. Your manager will look at how to settle this with you.',
    allLines: 'All lines',
    useExtra: 'Use extra hours',
  },
  ledger: {
    back: 'Back to the sum',
    title: 'All lines',
    crumb: 'Every line adds up to your balance. In brackets = awaiting approval, not counted yet.',
    entitlement: 'Entitlement {{year}}',
    holidayHours: 'Holiday hours',
    extraHours: 'Extra hours',
    from: 'from',
    nothingYet: 'Nothing yet',
  },
  voorlopig: {
    title: 'Why “provisional”?',
    intro: 'Your balance is probably <b>too high</b> right now. Two things are not finished yet:',
    oldBalance: 'Your old balance from {{year}}',
    oldBalanceNote: "HR is checking everyone's moved-over balances; after that it appears here as “carried over”.",
    stillOpen: 'still open',
    zeroHolidays: '{{count}} holidays counted 0 h',
    zeroHolidaysNote: "your roster from before {{date}} isn't in the system yet",
    view: 'view',
    outro: 'Go ahead and plan, but expect a lower number once this is done. You will see exactly what changes here, with the reason.',
  },
  answer: {
    title: 'Question from your manager',
    yourAnswer: 'Your answer',
    convertTitle: 'It was care for someone (sick child)',
    convertBody: 'Turn it into a priority care request and withdraw this extra time. Your manager decides how it is settled.',
    later: 'Later',
    send: 'Send answer',
    sent: 'Answer sent to your manager',
    sentPriority: 'Sent to your manager as a priority request',
  },
  year: {
    clocks: {
      daysToDec: 'days to 31 Dec<br/>year hours reset to 0',
      dec31: '31 Dec',
      choiceMade: 'extra-statutory choice made',
      chooseExtra: 'choose what happens to your extra-statutory hours',
    },
    yearHours: 'Year hours',
    intro: 'Your contract counts in year hours: each month you may work a bit more or less, and on 31 December it goes back to 0.',
    rosterVsContract: 'Roster different from contract',
    rosterVsContractNote: '{{date}} removed from your roster (−{{hours}}) · measured since {{from}}',
    patternVsContract: 'Roster compared with contract',
    extraHours: 'Extra hours',
    extraHoursNote: 'approved, minus what you already used',
    yearHoursNow: 'Year hours now',
    awaitingApproval: 'Awaiting approval',
    then: 'then',
    onDec31: 'On 31 December',
    in: 'in',
    days: 'days',
    minusLapse: 'if there are minus hours, they lapse. <b>Teddy Kids covers them</b>, not you.',
    plusAgree: 'if there are plus hours, you agree with your manager: money or time off.',
    exactlyZero: 'it is exactly 0.',
    limit: 'Limit',
    capBso: '1 month on average, because you also work in after-school care',
    capKdv: '2 weeks on average',
    byMonth: '{{year}} by month',
    legend: {
      holiday: 'holiday',
      zeroHoliday: 'holiday that counted 0 h',
      requested: 'requested',
      bottomNumber: 'number at the bottom = year hours at month end (— = no roster)',
    },
    monthHoliday: 'holiday',
    monthRequested: 'requested',
    monthYearHours: 'year hours',
    noRoster: 'no roster',
    nothingThisMonth: 'Nothing this month.',
    rosterFromPin: '{{date}}: from here your roster is in the system.',
    dec31Pin: '31 Dec: year hours reset to 0 · extra-statutory choice takes effect.',
    statutoryLot: 'Statutory {{year}}',
    extraLot: 'Extra-statutory {{year}}',
    incl: 'incl.',
    fromExtraHours: 'from extra hours',
    chooseBefore: 'Choose before 31 December',
    chooseQuestion: 'What should happen to the extra-statutory hours you have left at the end of {{year}}?',
    choice: {
      save: { title: 'Save', body: 'Kept for 5 years · max 330 h in total' },
      take: { title: 'Still take them', body: 'Plan them as holiday before 31 December' },
    },
    yourChoice: 'Your choice',
    changeUntil: 'You can change this until 31 December.',
    caoDefault: "If you don't choose, we pay them out on 31 December. That's what the CAO says.",
    choiceSaved: 'Choice saved',
  },
  errors: {
    PLUS_HOURS_INSUFFICIENT: 'Not enough extra hours: {{needed}} h needed, {{available}} h available.',
    PLUS_HOURS_INSUFFICIENT_plain: 'Not enough extra hours available.',
    VACATION_BALANCE_INSUFFICIENT: 'The vacation balance goes negative: {{after}} h after this decision. Tick "Approve anyway" to continue.',
    VACATION_BALANCE_INSUFFICIENT_plain: 'The vacation balance goes negative. Tick "Approve anyway" to continue.',
    COLLISION_FULL_LEAVE_DAY: 'This day is a full day of leave: it would count twice.',
    DECISION_NOTE_REQUIRED: 'A decline needs a reason.',
    DECISION_MINUTES_EXCEED: 'You cannot add more than the requested hours to holiday hours.',
    DECISION_KIND_UNSUPPORTED: 'Only worked time (extra time or a missed break) can go to holiday hours.',
    REQUEST_NOT_PENDING: 'This request is no longer waiting for a decision.',
    REQUEST_NOT_FOUND: 'This request no longer exists, or you cannot open it.',
    DECIDE_FORBIDDEN: 'Only a manager can decide this.',
    SELF_DECIDE_FORBIDDEN: 'Another manager decides on your own request.',
  },
  manager: {
    viewMeta: 'Manager view · contract {{hours}} h/week · {{group}}',
    freeToTake: 'free to take',
    extraHours: 'extra hours',
    open: 'open',
    yearHours: 'year hours',
    sameNumbers:
      'The same numbers {{name}} sees in her app: one calculation for everyone. With every approval you choose how the hours are settled; {{name}} sees that decision on her line.',
    flagsTitle: 'Note.',
    zeroBody_one: "{{count}} of {{name}}'s holidays from 1 Aug counted 0 h.",
    zeroBody_other: "{{count}} of {{name}}'s holidays from 1 Aug counted 0 h.",
    doubleBooked: '{{date}} is booked twice.',
    yearEndChoice: 'Choice: {{choice}}',
    yearEndNone: 'no choice yet → stays as time off',
    payoutStored: 'chosen before 30 Sep; not paid out',
    ratify: {
      button: 'Confirm',
      title: 'Confirm Portabase balance?',
      crumbs: '{{name}} · as of 20 Jul 2026',
      total: 'Portabase balance 20 Jul = LMS opening',
      after: 'Confirming only confirms this box. No number changes: not in the days, not in Our calculation, not on My Week.',
      cancel: 'Cancel',
      done: 'Confirmed by {{name}} · {{date}}',
      unknownUser: 'unknown user',
      toast: 'Confirmed · {{date}}',
      fixInVerlofcentrum: 'Correct opening balances in the Leave centre',
    },
    waitingForYou: 'Waiting for you',
    allDecided: 'Everything is decided.',
    priority: 'Priority',
    clashHoliday: 'Clash: holiday day',
    noFixedRoster: 'No fixed roster',
    noFixedRosterHint: 'Hours still 0 — set up the roster first. Approving is fine; the balance counts the hours once the fixed roster exists.',
    afterContractEnd: 'After contract end',
    afterContractEndHint: 'Contract runs until {{date}}. Approving is fine; the days count once the contract is extended.',
    goesNegative: '⚠ goes negative {{hours}}',
    negativeOverride: 'Approve anyway: vacation balance becomes {{hours}}',
    negativeOverrideHint: 'The balance goes below zero. Your choice is recorded with the decision.',
    openingInReview: 'opening balance in review',
    dayRemoved: 'day was removed from roster',
    questionAsked: 'question asked',
    jevAdvice: 'Advice from Jev, you decide',
    jev: { readsAsLeave: 'Reads as leave', sameDayHoliday: 'Same day as holiday', askTeacher: 'Ask the teacher' },
    shift: 'Shift',
    answer: 'answer',
    openSince: 'open since',
    days: 'days',
    fromExtraHours: 'from extra hours',
    howToSettle: 'How to settle?',
    settle: {
      asFiled: 'As requested',
      vacation: 'Holiday hours',
      extra: 'Extra hours',
      none: 'Not from balance (care / special)',
    },
    effect: {
      noneLeaveAsExtra: '0 h · it was leave (care / special), not extra',
      noneNotFromBalance: '0 h · not from her balance (e.g. site closed, funeral)',
      noneCareSpecial: '0 h · care / special leave',
      vacationPlus: '+{{hours}} added to her holiday hours (extra-statutory)',
      vacationMinus: '−{{hours}} from her holiday hours → {{after}} free',
      extraPlus: '+{{hours}} to her extra hours → {{after}}',
      extraMinus: '−{{hours}} from her extra hours → {{after}}',
      goesNegative: ' (goes negative)',
    },
    defaultLabel: {
      vacation: 'holiday hours',
      extra: 'extra hours',
      none: 'not from balance',
      split: '{{vacation}} holiday + {{extra}} extra',
    },
    collisionDisabled: 'Not possible: also a full holiday day, it would count twice',
    seesDecision: '{{name}} sees your decision on her request, on the day and in her overview.',
    reasonRequired: 'Reason (required, {{name}} sees this)',
    questionTo: 'Your question to {{name}}',
    noteOptional: 'Note (optional)',
    cancel: 'Cancel',
    decline: 'Decline',
    sendQuestion: 'Send question',
    approve: 'Approve',
    askQuestion: 'Ask a question',
    collisionHint: 'Also a full holiday day: can only be approved as “not from balance”, otherwise it counts twice.',
    approvedToast: 'Approved',
    toastSettle: { vacation: 'holiday hours', extra: 'extra hours', none: 'not from balance', split: 'as requested' },
    declinedToast: 'Declined, with reason',
    questionSentToast: 'Question sent to {{name}}',
    requested: 'requested',
    processedChip: 'processed',
    processedButton: 'Processed',
    markedProcessed: 'Marked as processed',
    groupLog: {
      title: 'Log once for the group',
      body: 'Team meeting, coaching evening or open day: you log it once, everyone who attended gets it approved in their extra hours.',
      what: 'What',
      date: 'Date',
      from: 'From',
      until: 'Until',
      defaultLabel: 'Team meeting',
      attendeesHint: 'In the app, everyone rostered on {{group}} that day is listed here',
      logFor_one: 'Log for {{count}} team member',
      logFor_other: 'Log for {{count}} team members',
      onHoliday: '{{name}} is on holiday on {{date}}: not logged',
      logged_one: 'Logged for {{count}} team member',
      logged_other: 'Logged for {{count}} team members',
      selfExcluded: 'you — log your own hours via Extra time worked',
    },
  },
  hero: {
    label: 'Free to take',
    aria: 'Why this number',
    approxDays: '≈ {{n}} days',
    legend: { taken: 'Taken', planned: 'Planned', requested: 'Requested', free: 'Still free' },
    pendingLine: '<b>{{hours}} awaiting approval</b>, not deducted yet. After approval: <b>{{after}}</b>.',
    boxLine: 'Start <b>{{start}}</b> (Portabase box) · leave in LMS from 1 Aug <b>{{leave}}</b>',
    accruedLine: 'Built up to today {{accrued}} of {{grant}} yearly entitlement',
    ask: 'Ask for time off',
  },
  yearEnd: {
    title: 'Choose before 31 December',
    daysLeft: '{{n}} days left',
    question: 'What would you like to do with your <b>{{hours}} extra-statutory hours {{year}}</b>?',
    options: {
      use: { title: 'Take as time off', sub: 'before 31 Dec' },
      save: { title: 'Save', sub: 'stays until you take it' },
      payout: { title: 'Pay out' },
    },
    none: 'What you don’t take stays as time off.',
    use: 'You’ll still take them. What you don’t take stays as time off.',
    save: 'Saved. Total saved: <b>{{total}}</b> of max. {{cap}} h.',
    saveProvisional: 'Saved. Total saved: <b>{{total}}</b> of max. {{cap}} h (Portabase balance still provisional).',
    saved: 'Choice saved. You can change this until 31 Dec.',
  },
  cutover: {
    blocks: { box: 'Portabase · 20 Jul 2026', days: 'Day by day from 1 Aug · LMS only', calc: 'Our calculation' },
    box: {
      title: 'Portabase balance',
      confirmed: 'confirmed {{date}}',
      source: 'As of 20 Jul 2026 · taken over from Portabase · frozen: LMS never recalculates this',
      inAdvance: 'taken in advance',
      showZero: '+ show {{count}} pots at 0 h',
      hideZero: 'Hide {{count}} empty pots',
      excluded: 'Extra-statutory hours up to 2024 were paid out under the old rules and don’t count.',
      opening: 'LMS opening · 1 Aug 2026',
      openingSub: '= Portabase balance 20 Jul · sum of the {{count}} lines above, as imported',
      closing: 'Everything up to 31 Jul is inside this box and is never recalculated. Leave from 1 Aug that was already in Portabase is settled in here.',
      staysAsTimeOff: 'What you don’t take stays as time off.',
      other: 'Other Portabase balances (not free to take)',
      otherSub: '{{count}} section(s) · tap for the amount per section',
      otherNone: 'none in Portabase',
      confirmedDock: 'Portabase confirmed',
      withheld: {
        no_contract_hours: 'There are no contract hours for you in Employes yet. Ask your manager to arrange it.',
        flex_contract_unsupported: 'For a flex contract (0 hours) we don’t keep a holiday balance here.',
        no_contract_history: 'Your contract history from Employes is still missing. Ask your manager to arrange it.',
      },
      noBox: 'Your Portabase balance of 20 Jul isn’t here yet. HR will add it.',
      postCutoverHire: 'You started after 1 Aug, so you have no Portabase balance. You build up your hours in LMS.',
    },
    lotSheet: {
      crumbs: 'Portabase balance 20 Jul 2026 · exactly as imported',
      hours: 'Hours',
      kind: 'Kind',
      year: 'Year',
      status: 'Status',
      negative:
        'Taken in advance: Portabase shows more statutory leave taken than was built up. We show it exactly as Portabase has it and it counts that way in your opening balance; nothing is rounded or set to 0.',
      statutory: 'Statutory leave: the legal minimum. Your hours stay until you take them.',
      extra: 'Extra-statutory leave from the CAO. What you don’t take stays as time off.',
      zero: 'This pot is at 0 h.',
    },
    otherSheet: {
      title: 'Other Portabase balances',
      crumbs: 'As of 20 Jul 2026 · as imported · not free to take, never counts in Free to take',
      section: 'section',
    },
    close: 'Close',
    days: {
      cols: { day: 'Day', roster: 'Roster', away: 'Away', extra: 'Extra', free: '± free' },
      sum: { roster: 'Roster', away: 'Away', extra: 'Extra', free: '± Free' },
      dayOff: 'day off · not rostered',
      noRoster: 'not rostered',
      planned: 'planned',
      holiday: 'holiday',
      holidayNoRoster: 'holiday · not rostered, 0 h',
      settled: 'already settled in Portabase',
      legend:
        'Away = holiday at your LMS roster of that day · ± free = what the day does to Free to take (leave already in Portabase: 0) · striped = awaiting approval, not counted yet',
    },
    calc: {
      title: 'Our calculation',
      intro: 'Starts at the Portabase box and adds only what happened in LMS from 1 Aug.',
      opening: 'Opening from the Portabase box',
      openingSub: '= Portabase balance 20 Jul, as imported',
      settled: 'Leave from 1 Aug from Portabase',
      settledSub: '{{count}} days · already settled in Portabase, counts 0',
      taken: 'Leave from 1 Aug (LMS hours) · taken',
      takenSub: '{{count}} days up to today · requested in LMS · roster of that day',
      planned: 'Leave from 1 Aug (LMS hours) · planned',
      plannedSub: '{{count}} days · approved, after today',
      requests: 'Requests settled as holiday hours',
      requestsSub: 'extra time and corrections from 1 Aug, manager decision',
      requestsPending: '{{count}} awaiting approval, not counted yet',
      requestsInside: 'already counted in the leave above',
      conversions: 'Extra hours turned into holiday hours',
      corrections: 'Corrections by HR',
      writtenOff: 'Written off by HR',
      extraTitle: 'Extra hours (meeruren)',
      negative: 'Below zero: Portabase shows more statutory leave taken than was built up (taken in advance). We show it as it is.',
      same: 'Same number as on My Week: <b>{{hours}}</b>',
    },
    dayList: {
      settledTitle: 'Already settled in Portabase',
      takenTitle: 'Taken from 1 Aug',
      plannedTitle: 'Planned',
      settledCrumbs: 'Already in Portabase on 20 Jul · away per your LMS roster, counts 0 from your balance',
      crumbs: 'Requested in LMS · holiday at your LMS roster of that day · once per day',
      total: 'Total',
      nothing: 'Nothing.',
    },
    nudge: { title: 'Plan your days off', body: 'your hours stay until you take them' },
  },
  reiskosten: {
    card: {
      title: '📍 Is your address still right?',
      source: 'From your staff file (Employes). We pay your travel allowance on this address.',
      kmChip: '🚲 {{km}} km one way → {{loc}}',
      perDayChip: '{{amount}} per day worked',
      kmLoading: '🚲 calculating km…',
      kmUnknown: 'km unknown — {{reason}}',
      locationMissing: 'main location is not linked yet',
      recalcFailed: '⚠️ recalculation failed',
      confirm: 'Correct ✓',
      edit: 'Change',
      explain:
        '{{kmDay}} km per day × {{rate}} (childcare CAO from 1 Jul 2026). Only days you actually work count. Moving? Tell us the date: from that day we use your new address.',
      confirmedToast: 'Address confirmed · {{date}}',
      kmTo: '{{km}} km → {{loc}}',
      kmToLoading: 'calculating km → {{loc}}',
      kmToUnknown: 'km unknown → {{loc}}',
      okLine: '📍 {{street}}, {{city}} · {{km}} · ',
      confirmedOn: 'confirmed {{date}}',
      pendingChip: '⏳ change sent',
      withdraw: 'Withdraw',
      withdrawnToast: 'Change withdrawn',
      nextLine: '🏡 From {{date}}: {{street}}, {{city}} · {{km}} · ',
      processedChip: 'processed by Antonella',
      noReisTitle: 'You do not receive a travel allowance.',
      noReisBody: 'Reason: {{reason}} (set {{date}}). Not right? Ask your manager.',
      noCurrent: 'No address applies today. Ask your manager to put your address in Employes.',
    },
    sheet: {
      title: 'Change address',
      intro: 'Antonella puts it in Employes; then we recalculate your km. Until then the old address applies.',
      street: 'Street + house number',
      zip: 'Postcode',
      city: 'City',
      from: 'Living here from',
      preview: 'New distance: <b>calculated after saving</b> · now {{km}}',
      cancel: 'Cancel',
      send: 'Send',
      sentToast: 'Sent to Antonella · km follow once processed',
      streetInvalid: 'Enter street and house number, for example Voorbeeldstraat 12.',
    },
    gewerkt: {
      distanceUnknown: 'Distance unknown',
      running: 'month still running',
      movedOn: 'moved on {{date}}',
      prev: 'Previous month',
      next: 'Next month',
      tiles: { days: 'days', hours: 'hours', km: 'km', money: 'travel' },
      cols: { day: 'Day', roster: 'Roster', worked: 'Worked', status: 'Status', km: 'Km' },
      week: 'Week {{n}}',
      planned: 'planned',
      status: { work: 'worked', extra: 'extra day', vac: 'vacation', sick: 'sick', hol: 'public holiday' },
      noReisTitle: 'no travel allowance',
      daysToToday_one: '{{count}} day up to today',
      daysToToday_other: '{{count}} days up to today',
      formula_one: '{{count}} day × {{km}} km × {{rate}}',
      formula_other: '{{count}} days × {{km}} km × {{rate}}',
      total: 'Travel allowance {{month}}',
      none: 'No travel allowance ({{reason}})',
      unknownKm_one: '{{count}} day without km (km unknown)',
      unknownKm_other: '{{count}} days without km (km unknown)',
      legendKm: 'km ✓ = home → {{loc}} × 2',
      hint: 'Your travel allowance goes with your {{month}} salary automatically. A day not right? Tap My Week and fix the hours, the km follow.',
    },
    errors: {
      NO_EMPLOYES_LINK: 'You are not linked to Employes yet. Ask your manager to arrange it.',
      NO_EMPLOYES_ADDRESS: 'There is no address in Employes yet. Ask your manager to enter your address.',
      EMPLOYES_ADDRESS_INCOMPLETE: 'Your address in Employes is incomplete. Ask your manager to complete it.',
      NO_MAIN_LOCATION: 'You have no main location yet. Ask your manager to set it.',
      NO_START_DATE: 'Your start date is still unknown. Fill it in first.',
      ADDRESS_INVALID: 'Enter street, house number, postcode and city.',
      EFFECTIVE_FROM_TOO_EARLY: 'That date is too far back (31 days at most).',
      REQUEST_ALREADY_PENDING: 'An address change is already waiting for Antonella.',
      ADDRESS_NOT_CONFIRMABLE: 'You cannot confirm this address.',
      REQUEST_NOT_PENDING: 'This change is no longer waiting: Antonella has already processed it.',
    },
  },
  gala: {
    posterAlt: 'Christmas Dinner 2026 poster',
    kicker: '🎄 Teddy Kids',
    title: 'Christmas Dinner 2026',
    sub: 'An evening to celebrate our wonderful team. Masquerade party!',
    chipDate: '📅 {{date}}',
    chipTime: '🕡 {{from}}–{{to}}',
    chipLocation: '📍 Secret location',
    chipCourses: '🍽 5 courses',
    chipDress: '🎭 Christmas Gala',
    question: 'Are you coming?',
    yes: "🎉 Yes, I'm in!",
    no: 'No, sadly',
    foot: '<b>{{n}}</b> colleagues already in · answer before 12 Dec',
    yesTitle: "You're in, {{name}}! See you 19 Dec 🥂",
    yesTitleNoName: "You're in! See you 19 Dec 🥂",
    yesSub: 'Sat 19 Dec · 18:30 · Location announced 1 hour before<br/>Dress code: Christmas Gala · masquerade',
    change: 'change',
    closed: 'Answers closed',
    reasonLabel: "Shame! Why can't you make it?",
    reasonHelp: 'Keep it short — at least 7 words, so we can maybe help out.',
    reasonPlaceholder: 'E.g. I work that evening at my second job and cannot swap…',
    counter: '{{n}} / {{min}} words',
    left: '{{n}} to go',
    enough: '✓ enough',
    back: 'Back',
    ok: 'OK, decline',
    noTitle: "Shame, we'll miss you!",
    noSub: "You're marked as not coming to the Christmas Dinner.<br/>Changed your mind? Possible until 12 Dec.",
    loadError: "Couldn't load the Christmas Dinner card.",
    retry: 'Retry',
    reasonTooShort: 'Please write at least 7 words.',
    saveFailed: 'Your answer was not saved ({{status}}).',
    managerLink: 'View answers →',
    modalTitle: 'Santa Teddy has a question…',
    modalHelp: 'A little Christmas invitation before you continue. Let us know if you are coming!',
    santaNo: "Santa Teddy says no no noooo… please choose if you're coming! 🎅🧸",
    stillThinking: 'Still thinking…',
    thinkingCount: 'Thinking clicks',
    thinkingHelp: 'No answer saved. Just a very busy thinking button.',
    thinkingAchievement: '🏅 {{n}} clicks! Certified Christmas thinker.',
    thinkingPro: '🏆 {{n}} clicks! Santa’s head of overthinking.',
    thinkingLegend: '🎊 {{n}} clicks! Legendary thinker. Santa still needs your answer!',
  },
  startDateGate: {
    title: 'When did you start at Teddy Kids?',
    help: 'Our HR system holds a placeholder date (1 September 2024) for everyone who joined before September 2024. We would love to know your real first working day at Teddy Kids.',
    why: 'That date counts for your anniversary and your reviews.',
    label: 'My first working day',
    save: 'Save',
    saving: 'Saving…',
    pickDate: 'Pick your first working day first.',
    outOfRange: 'Pick a date between 1 January 1990 and today.',
    saved: 'Thank you! Your start date is saved.',
  },
  notices: {
    label: 'Vacation messages',
    pill: { lapse_6m: 'Statutory notice', lapse_3m: 'Final notice' },
    ask: 'Ask for time off',
    later: 'Later',
    gotIt: 'Got it',
    emailNote: 'Also sent by email · stays here until you tap Got it',
    stayNote: 'Stays here until you tap Got it',
    loadError: "Your vacation messages couldn't load.",
    retry: 'Try again',
    ackFailed: "Couldn't save: {{message}}",
    log: {
      title: 'Notices · {{name}}',
      latest: 'latest: {{date}} · {{state}}',
      state: { read: 'read', dismissed: 'dismissed', seen: 'seen', unseen: 'not seen yet' },
      empty: 'No notices for {{name}} yet.',
      loadError: "The notices couldn't load.",
      col: { date: 'Date', kind: 'Kind', lot: 'Lot', hours: 'Hours in message', lapse: 'Lapses', email: 'Email', push: 'Push', seen: 'Seen', ack: 'Read' },
      kind: { nudge: 'reminder', lapse_6m: '6 mo', lapse_3m: '3 mo' },
      lot: 'statutory {{year}}',
      hoursFree: '{{hours}} free',
      email: {
        pending: 'sending',
        sent: 'sent',
        failed: 'failed · will be retried',
        failedNudge: 'failed',
        no_address: 'no email address',
        needs_check: 'unsure if sent',
        expired: 'not given',
        not_sent: 'not sent',
      },
      sentOn: 'sent {{date}}',
      push: {
        sent_one: '{{count}} device',
        sent_other: '{{count}} devices',
        none: 'no subscription',
        failed: 'failed',
        pending: 'sending',
        needs_check: 'unsure',
        not_sent: 'not sent',
      },
      dismissed: 'dismissed',
      footer: 'Nobody can delete a line. Seen and Read are stamped by the app, with the database clock.',
      help: {
        no_address: 'No email address: add it on the Staff File; the next run sends the notice after all.',
        needs_check: 'Unsure if sent: check in Resend whether the mail arrived. If not, give the notice in writing yourself; the app does not send it again.',
        expired: 'Not given: the lapse date passed or the employment ended before the mail arrived. This notice does not count as given.',
      },
    },
  },
  room: {
    line: '{{day}}: {{room}} ({{groups}}) · {{building}} · {{kids}} children',
    roomName: '{{label}} room',
    swimRoomName: 'Swimmers ({{label}})',
    noteTitle: 'Combined day',
    building: 'Building',
    groups: 'Groups together',
    kids: 'Children',
    kidsValue: '{{n}} children',
    swimmersLeaving: '{{n}} swimmers from these groups go swimming',
    together: 'Until {{time}} together in the {{room}}',
    shift: 'Your time here: {{start}}–{{end}}',
    close: 'tap outside to close',
  },
};
